import React, { ReactNode } from 'react';
import './styled.css';
import { Control, Controller, FieldError } from 'react-hook-form';
import Icons from '../Icons';
import { Input } from 'antd';

interface InputFieldProps {
  /**
   * The control object comes from `useForm` and is used to register the input
   * field so that it can be validated.
   */
  control: Control<any>;
  /** 
   * An error message will be shown below the input field which displays a short hint 
   * to the user about what they should enter in the input.
   */
  error?: FieldError | string;
  /** 
   * A label will be shown above the input field which displays a short hint 
   * to the user about what they should enter in the input.
   * @default null
   */
  label?: string | React.ReactElement;
  /** 
   * Validation rules for the input field. `Formatting with React Hook Form`
   * @default null
   */
  rules?: {
    required?: any;
    [key: string]: any;
  };
  /** 
   * A placeholder for the input field which displays a short hint 
   * to the user about what they should enter in the input.
   * @default '
   */
  placeholder?: string;
  /** 
   * A hint will be shown below the input field which displays a short hint for the user about what they should enter in the input.
   * @default null
   */
  hint?: string;
  /**
   * The name of the input field. `Hook Form Dependency`
   * @default ''
   */
  name: string;
  /**
   * The type of the input field.
   * @default 'text'
   */
  type?: string;
  /**
   * The icon to be shown on the left or right of the input field.
   * @default null
   */
  icon?: ReactNode;
  /**
   * Whether the input field is required or not. If set to true, it will
   * be marked as required on the form.
   * @default false
   */
  iconDirection?: 'left' | 'right' | 'insideLeft' | 'insideRight';
  /**
   * The function to be called when the input field is clicked.
   * @default null
   */
  onClick?: () => void;

  /**
   * Additional class names for the input field.
   * @default ''
   */
  className?: string;
  /**
   * Whether the input field is disabled or not.
   * @default false
   */
  disabled?: boolean;
  /**
   * The default value of the input field when it is first rendered.
   * @default ''
   */
  defaultValue?: string;
  /**
   * A custom loading icon which can be used instead of the default
   * loading icon.
   * @default null
   */
  customLoadingIcon?: ReactNode;
  /**
   * Whether the input field is in a loading state or not. If set to true,
   * a loading icon will be shown.
   * @default false
   */
  isLoading?: boolean;
  /**
   * The function to be called when the input field is pressed.
   * @default null
   */
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * The function to be called when the input field changes.
   * @default null
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputField: React.FC<InputFieldProps & React.ComponentProps<typeof Input>> = ({
  control,
  error,
  label,
  rules,
  placeholder = '',
  hint,
  name,
  type = 'text',
  icon,
  iconDirection,
  onClick,
  className = '',
  disabled,
  defaultValue,
  customLoadingIcon,
  isLoading,
  onKeyPress,
  onChange,
  ...props
}) => {
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  const handleOnClick = () => {
    onClick?.();
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e); 
  }

  return (
    <div className={`flex flex-col w-full ${className}`}>
      <div className="relative flex flex-col w-full text-sm tracking-tight leading-5 text-gray-500">
        <Controller
          name={name}
          rules={rules}
          control={control}
          render={({ field, fieldState: { error: fieldError } }) => (
            <>
              {(label || rules?.required) && (
                <span className='mb-1'>
                  {label && (
                    <label htmlFor={"input" + name} className="text-xs sm:text-sm font-medium text-[#414651]">
                      {label}
                    </label>
                  )}
                  {rules?.required && label &&<span className="pl-1 text-green-500">*</span>}
                </span>
              )}
              <Input
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleOnChange(e);
                }}
                id={"input" + name}
                type={passwordVisible ? 'text' : type}
                className='rounded-full border border-gray-400 w-full px-3 sm:px-4 py-2 sm:py-2.5 tracking-tight leading-5 text-sm sm:text-base text-gray-900 shadow-sm'
                placeholder={placeholder}
                suffix={type === 'password' ? <Icons
                  icon={passwordVisible ? "heroicons-outline:eye" : "heroicons-outline:eye-off"}
                  className="h-4 w-4 sm:h-[1.1rem] sm:w-[1.1rem] flex-shrink-0 cursor-pointer hover:text-gray-800 transition-all"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                /> : iconDirection === 'right' && (isLoading && customLoadingIcon ? customLoadingIcon : icon)}
                prefix={iconDirection === 'left' && icon}
                disabled={disabled}
                defaultValue={defaultValue}
                onPressEnter={onKeyPress}
                status={fieldError ? 'error' : undefined}
                {...props}
              />
              {(fieldError || error) && <p className="text-xs tracking-tight leading-5 text-red-500">{typeof error === 'string' ? error : (fieldError || error)?.message}</p>}
            </>
          )}
        />
        {hint && <p className="mt-1.5 text-xs tracking-tight leading-5 text-gray-600">{hint}</p>}
      </div>
    </div>

  );
}

export default InputField;